/**
 * API Error Handler Utility
 * 
 * Provides utility functions for handling API errors consistently
 */

/**
 * Sanitize error message to remove sensitive information
 * @param {string} message - Raw error message
 * @returns {string} Sanitized error message
 */
const sanitizeErrorMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return 'An error occurred while processing your request';
  }

  // Remove stack traces, file paths, and internal details
  let sanitized = message
    // Remove file paths (e.g., /var/www/html/app/...)
    .replace(/\/[^\s]+\.(php|js|ts|jsx|tsx):\d+/gi, '')
    // Remove stack trace indicators
    .replace(/Stack trace:.*/is, '')
    .replace(/at .*\(.*\)/g, '')
    // Remove Laravel/Symfony internal error details
    .replace(/RouteNotFoundException|MethodNotAllowedException|NotFoundHttpException/gi, '')
    .replace(/Symfony\\Component\\Routing\\Exception\\/gi, '')
    .replace(/Illuminate\\/gi, '')
    // Remove vendor paths
    .replace(/vendor\/.*/gi, '')
    // Remove line numbers and code snippets
    .replace(/line \d+/gi, '')
    .replace(/on line \d+/gi, '')
    // Remove exception class names
    .replace(/Exception:.*/gi, '')
    // Clean up multiple spaces
    .replace(/\s+/g, ' ')
    .trim();

  // If message contains sensitive patterns, return generic message
  const sensitivePatterns = [
    /Route \[.*\] not defined/i,
    /SQLSTATE/i,
    /PDOException/i,
    /database/i,
    /connection/i,
    /query/i,
    /syntax error/i,
    /parse error/i,
    /fatal error/i,
  ];

  // Check if message contains sensitive information
  const containsSensitiveInfo = sensitivePatterns.some(pattern => pattern.test(sanitized));

  if (containsSensitiveInfo || sanitized.length === 0) {
    return 'An error occurred while processing your request. Please try again later.';
  }

  // Limit message length to prevent information leakage
  if (sanitized.length > 200) {
    sanitized = sanitized.substring(0, 200) + '...';
  }

  return sanitized;
};

/**
 * Check if the request was aborted client-side because it exceeded the configured
 * timeout (REACT_APP_API_TIMEOUT). Axios reports this as ECONNABORTED, or as
 * ETIMEDOUT when transitional.clarifyTimeoutError is enabled.
 * Distinct from a genuine network failure: the server was reachable, it just never replied.
 * @param {Error} error - The error object from axios
 * @returns {boolean}
 */
export const isTimeoutError = (error) => {
  if (error?.response) return false;

  return error?.code === 'ECONNABORTED' ||
    error?.code === 'ETIMEDOUT' ||
    /timeout/i.test(error?.message || '');
};

/**
 * Extract error message from API error response
 * @param {Error} error - The error object from axios
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  if (error.response) {
    // Server responded with error status
    const { data, status } = error.response;

    // For server errors (500+), always return generic message
    if (status >= 500) {
      return 'An error occurred on the server. Please try again later or contact support if the problem persists.';
    }

    // For client errors, sanitize the message
    let message = '';

    // Check various possible error message formats
    // Prioritize 'error' field inside 'data' object (User specific request)
    // Structure: { data: { error: "..." }, message: "..." } -> "..."
    if (data?.data?.error) {
      message = data.data.error;
    }
    // Structure: { error: "..." }
    else if (data?.error) {
      message = data.error;
    }
    // Structure: { message: "..." }
    else if (data?.message) {
      message = data.message;
    } else if (data?.errors && Array.isArray(data.errors)) {
      message = data.errors.join(', ');
    } else if (data?.errors && typeof data.errors === 'object') {
      message = Object.values(data.errors).flat().join(', ');
    }

    // Sanitize the message to remove sensitive information
    if (message) {
      return sanitizeErrorMessage(message);
    }

    // Default messages based on status code
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication required. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 422:
        return 'Validation error. Please check your input.';
      case 429:
        return 'Too many requests. Please try again later.';
      default:
        return 'An error occurred while processing your request.';
    }
  } else if (isTimeoutError(error)) {
    // Request was aborted client-side after exceeding the configured timeout.
    // The server is reachable but did not answer in time - do not blame the network.
    return 'The server is taking too long to respond. Please try again in a few minutes.';
  } else if (error.request) {
    // Request made but no response
    return 'Unable to reach the server. Please check your network connection and try again.';
  } else {
    // Error in request setup - sanitize the message
    return sanitizeErrorMessage(error.message) || 'An unexpected error occurred';
  }
};

/**
 * Extract validation errors from API response
 * @param {Error} error - The error object from axios
 * @returns {Object} Object with field names as keys and error messages as values
 */
export const getValidationErrors = (error) => {
  if (error.response?.status === 422 && error.response?.data?.errors) {
    const errors = error.response.data.errors;

    // Handle different error formats
    if (typeof errors === 'object' && !Array.isArray(errors)) {
      // Convert nested arrays to strings
      return Object.entries(errors).reduce((acc, [key, value]) => {
        acc[key] = Array.isArray(value) ? value.join(', ') : value;
        return acc;
      }, {});
    }
  }

  return {};
};

/**
 * Check if error is authentication related
 * @param {Error} error - The error object from axios
 * @returns {boolean}
 */
export const isAuthError = (error) => {
  return error.response?.status === 401;
};

/**
 * Check if error is permission related
 * @param {Error} error - The error object from axios
 * @returns {boolean}
 */
export const isPermissionError = (error) => {
  return error.response?.status === 403;
};

/**
 * Check if error is validation related
 * @param {Error} error - The error object from axios
 * @returns {boolean}
 */
export const isValidationError = (error) => {
  return error.response?.status === 422;
};

/**
 * Check if error is server related
 * @param {Error} error - The error object from axios
 * @returns {boolean}
 */
export const isServerError = (error) => {
  const status = error.response?.status;
  return status >= 500 && status < 600;
};

/**
 * Check if error is network related
 * @param {Error} error - The error object from axios
 * @returns {boolean}
 */
export const isNetworkError = (error) => {
  return !error.response && error.request;
};

/**
 * Display error notification
 * This is a placeholder - integrate with your toast/notification library
 * @param {Error} error - The error object from axios
 */
export const showErrorNotification = (error) => {
  const message = getErrorMessage(error);

  // Only log detailed errors in development mode
  if (process.env.NODE_ENV === 'development') {
    console.error('Error:', error);
  } else {
    // In production, only log sanitized message
    console.error('Error:', message);
  }

  return message;
};

/**
 * Handle API error with standard response
 * @param {Error} error - The error object from axios
 * @param {Object} options - Options for error handling
 * @returns {Object} Standardized error object
 */
export const handleApiError = (error, options = {}) => {
  const {
    showNotification = true,
    defaultMessage = 'An error occurred',
  } = options;

  // Extract and sanitize error message - prioritize 'error' field from backend response
  const rawErrorMessage = error.response?.data?.data?.error || error.response?.data?.error || error.response?.data?.message || '';
  const errorMessage = rawErrorMessage ? sanitizeErrorMessage(rawErrorMessage) : (getErrorMessage(error) || defaultMessage);
  const backendMessage = error.response?.data?.message ? sanitizeErrorMessage(error.response.data.message) : null;

  const errorResponse = {
    success: false,
    error: errorMessage, // Use 'error' field as primary error message
    message: errorMessage, // Ensure message strictly follows the prioritized errorMessage
    validationErrors: getValidationErrors(error),
    status: error.response?.status,
    isAuthError: isAuthError(error),
    isPermissionError: isPermissionError(error),
    isValidationError: isValidationError(error),
    isServerError: isServerError(error),
    isNetworkError: isNetworkError(error),
    isTimeoutError: isTimeoutError(error),
  };

  if (showNotification) {
    showErrorNotification(error);
  }

  return errorResponse;
};

export default {
  getErrorMessage,
  getValidationErrors,
  isAuthError,
  isPermissionError,
  isValidationError,
  isServerError,
  isNetworkError,
  isTimeoutError,
  showErrorNotification,
  handleApiError,
  sanitizeErrorMessage,
};

